import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const SOCType1_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            SOC Type 1 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            System and Organization Controls Type 1 — Point-in-time audit of
            control design and implementation for security, availability,
            confidentiality, and privacy.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              SOC Type 1 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              SOC Type 1 (System and Organization Controls Type 1) is a service
              organization audit report designed to assess the controls and
              procedures an organization has in place to manage and secure
              customer data. SOC Type 1 reports focus on an organization’s
              control environment at a specific point in time, detailing the
              design and effectiveness of the controls to meet security,
              availability, confidentiality, and privacy objectives.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              SOC Type 1 certification is essential for organizations that
              handle sensitive customer data or provide outsourced services,
              such as cloud computing, data hosting, or third-party outsourcing.
              The certification assures clients, stakeholders, and regulatory
              bodies that an organization’s controls and processes are effective
              at the time of the audit, demonstrating a commitment to
              maintaining robust data security and compliance practices.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is SOC Type 1?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              SOC Type 1 is an audit report that assesses the design and
              implementation of a service organization’s internal controls
              related to financial reporting, information security, data
              privacy, and other critical processes. The key distinction of a
              SOC Type 1 report is that it evaluates controls at a specific
              point in time, rather than over a period of time (as is the case
              with SOC Type 2).
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              The SOC Type 1 report covers several key aspects:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Control Design: Assessing whether the organization has the appropriate policies, procedures, and practices in place to safeguard client data and achieve the defined objectives.",
                "Control Implementation: Reviewing how the organization has implemented controls and ensuring they are properly functioning at the time of the audit.",
                "Service Organization’s Responsibilities: Defining the scope of controls under the service organization’s purview and how they relate to the overall security and confidentiality of client data.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-600 text-lg leading-relaxed mt-6">
              SOC Type 1 is suitable for organizations seeking to assure clients
              and third-party vendors about their control environment for the
              security and privacy of information at a given point in time. It
              helps organizations build trust and transparency with their
              stakeholders by demonstrating that they have been assessed by an
              independent third-party auditor.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of SOC Type 1 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Point-in-Time Assessment",
                desc: "SOC Type 1 evaluates the design and implementation of an organization’s controls at a specific point in time, providing a snapshot of the service organization’s control environment.",
              },
              {
                icon: Target,
                title: "Audit of Control Design",
                desc: "The primary focus of SOC Type 1 is the design of controls rather than their effectiveness over time. The audit ensures that the organization’s controls are set up to meet security, confidentiality, and availability objectives.",
              },
              {
                icon: Globe,
                title: "Comprehensive Coverage",
                desc: "The report evaluates a wide range of control areas, including information security (safeguarding data and IT infrastructure), availability (ensuring system uptime and reliability), confidentiality (protecting sensitive data from unauthorized access), and privacy (complying with data privacy laws and protecting personal information).",
              },
              {
                icon: Award,
                title: "Independent Third-Party Validation",
                desc: "SOC Type 1 reports are conducted by independent third-party auditors who assess the control environment of the service organization. This external validation helps build trust with stakeholders, assuring them that the organization’s controls are well-designed.",
              },
              {
                icon: Users,
                title: "Assurance for Stakeholders",
                desc: "SOC Type 1 reports provide clients, investors, regulators, and other stakeholders with confidence that the organization’s control environment is designed to meet regulatory and security requirements. It assures stakeholders that sensitive data is being protected appropriately.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why SOC Type 1 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Demonstrates Control Effectiveness — SOC Type 1 certification demonstrates to your clients, stakeholders, and regulators that you have appropriate controls in place to safeguard sensitive data and protect business operations.",
                "Builds Trust with Clients and Stakeholders — By obtaining SOC Type 1 certification, you show a commitment to security, transparency, and compliance, helping to establish long-term relationships with clients and partners.",
                "Compliant with Industry Standards — SOC Type 1 reports are widely recognized and often required by clients, regulatory bodies, and industry standards. Achieving certification demonstrates your adherence to industry best practices for security, availability, confidentiality, and privacy.",
                "Improves Vendor Management — For organizations that rely on third-party vendors to provide outsourced services, SOC Type 1 certification ensures that your vendors meet stringent security and control requirements, enhancing your vendor management practices.",
                "Competitive Advantage — Achieving SOC Type 1 certification gives your business a competitive edge in the marketplace by differentiating you from competitors who may not have undergone similar audits. It demonstrates your ability to handle client data securely and meet industry standards.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does SOC Type 1 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              SOC Type 1 reports assess the control design and implementation at
              a specific point in time. The key elements of the certification
              include:
            </p>
            <div className="space-y-4">
              {[
                "1. Control Design — The audit evaluates whether the service organization has the appropriate controls in place to protect client data, ensure security, and meet other objectives like confidentiality, availability, and privacy.",
                "2. Control Implementation — The report reviews how the organization has implemented these controls at a particular point in time to ensure they are functioning as intended.",
                "3. Scope of the Audit — The scope of the SOC Type 1 audit covers various control areas, including information security (e.g., IT infrastructure, data protection), availability (e.g., service uptime, system reliability), confidentiality (e.g., data access, encryption), and privacy (e.g., handling of personal data and compliance with regulations like GDPR).",
                "4. Independent Audit Report — The final report is issued by an independent third-party auditor, providing an unbiased assessment of the service organization’s control environment.",
                "5. Assurance for Stakeholders — The SOC Type 1 report provides clients, investors, regulators, and other stakeholders with an assurance that the service organization’s controls are in place and functioning at the time of the audit.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of SOC Type 1 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Enhanced Customer Trust",
                "Regulatory Compliance",
                "Improved Operational Efficiency",
                "Vendor Risk Management",
                "Competitive Advantage",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve SOC Type 1 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              To achieve SOC Type 1 certification, organizations must follow
              these key steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Develop and Implement Controls — Design and implement the necessary security controls for your organization’s IT infrastructure, ensuring they address confidentiality, security, and privacy requirements.",
                "2. Prepare for the Audit — Review the scope of the SOC Type 1 audit and ensure that all relevant policies, processes, and controls are in place to meet the criteria established by the auditors.",
                "3. Engage an Independent Auditor — Work with an accredited third-party auditor who will assess the design and implementation of your security controls and processes.",
                "4. Provide Necessary Documentation — Provide the auditor with the necessary documentation to assess your organization’s control environment and provide evidence that your controls meet the defined requirements.",
                "5. Third-Party Audit and Report — The auditor will conduct the audit, assess your organization’s control design, and produce a SOC Type 1 report that provides independent assurance to your clients and stakeholders.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for SOC Type 1 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. offers expert SOC Type 1 certification
              consultancy services, helping businesses design and implement
              security controls that meet industry standards for data
              protection, availability, and confidentiality.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "SOC Type 1 Readiness Assessment",
                "Control Design and Implementation Guidance",
                "Third-Party Audit and Report Preparation",
                "Internal Audit and Pre-certification Support",
                "Continuous Monitoring and Improvement",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve SOC Type 1 certification, ensuring
              robust security controls, data protection, and regulatory
              compliance.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default SOCType1_Details;
